/**
 * Orca Account Web - Settings Module
 * Handles user settings and preferences management
 */

import { createClient } from '@supabase/supabase-js';

// Configuration
const CONFIG = {
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL || 'https://api.orcaslicer.com',
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
};

// Initialize Supabase client
let supabase;
try {
  supabase = createClient(CONFIG.supabaseUrl, CONFIG.supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true
    }
  });
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
}

// DOM Elements
const notificationForm = document.getElementById('notification-form');
const privacyForm = document.getElementById('privacy-form');
const displayForm = document.getElementById('display-form');
const signoutBtn = document.getElementById('signout-btn');
const showApiKeyBtn = document.getElementById('show-api-key');
const regenerateApiKeyBtn = document.getElementById('regenerate-api-key');
const configureWebhooksBtn = document.getElementById('configure-webhooks');
const exportAllDataBtn = document.getElementById('export-all-data');
const messageBox = document.getElementById('message');

// State
let currentUser = null;
let userSettings = null;
let apiKeyVisible = false;

/**
 * Initialize settings page
 */
async function initSettings() {
  if (!supabase) {
    showError('Failed to initialize settings page. Please refresh the page.');
    return;
  }

  // Check authentication
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error || !session) {
    // Redirect to sign in page
    window.location.href = '/';
    return;
  }

  currentUser = session.user;

  // Set up event listeners
  setupEventListeners();

  // Load settings
  await loadSettings();

  // Listen for auth changes
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      window.location.href = '/';
    }
  });
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
  // Settings forms
  if (notificationForm) {
    notificationForm.addEventListener('submit', handleNotificationSettings);
  }

  if (privacyForm) {
    privacyForm.addEventListener('submit', handlePrivacySettings);
  }

  if (displayForm) {
    displayForm.addEventListener('submit', handleDisplaySettings);
  }

  // API controls
  if (showApiKeyBtn) {
    showApiKeyBtn.addEventListener('click', handleShowApiKey);
  }

  if (regenerateApiKeyBtn) {
    regenerateApiKeyBtn.addEventListener('click', handleRegenerateApiKey);
  }

  if (configureWebhooksBtn) {
    configureWebhooksBtn.addEventListener('click', handleConfigureWebhooks);
  }

  if (exportAllDataBtn) {
    exportAllDataBtn.addEventListener('click', handleExportAllData);
  }

  if (signoutBtn) {
    signoutBtn.addEventListener('click', handleSignOut);
  }

  // Theme change detection
  const themeSelect = document.getElementById('theme-select');
  if (themeSelect) {
    themeSelect.addEventListener('change', handleThemeChange);
  }
}

/**
 * Load user settings
 */
async function loadSettings() {
  try {
    // Load settings from database
    const { data, error } = await supabase
      .from('settings')
      .select('*')
      .eq('user_id', currentUser.id);

    if (error) {
      throw error;
    }

    // Convert to key-value object
    userSettings = {};
    data.forEach(setting => {
      userSettings[setting.k] = setting.v;
    });

    // Populate forms with current settings
    populateSettingsForms();

  } catch (error) {
    console.error('Failed to load settings:', error);
    // Use default settings if loading fails
    loadDefaultSettings();
  }
}

/**
 * Load default settings
 */
function loadDefaultSettings() {
  userSettings = {
    // Notification settings
    email_notifications: true,
    marketing_emails: false,
    security_alerts: true,

    // Privacy settings
    profile_public: false,
    usage_analytics: true,
    crash_reports: true,

    // Display settings
    theme: 'auto',
    language: 'en',
    timezone: 'auto'
  };

  populateSettingsForms();
}

/**
 * Populate settings forms with current values
 */
function populateSettingsForms() {
  // Notification form
  if (notificationForm) {
    setCheckboxValue(notificationForm.email_notifications, userSettings.email_notifications);
    setCheckboxValue(notificationForm.marketing_emails, userSettings.marketing_emails);
    setCheckboxValue(notificationForm.security_alerts, userSettings.security_alerts);
  }

  // Privacy form
  if (privacyForm) {
    setCheckboxValue(privacyForm.profile_public, userSettings.profile_public);
    setCheckboxValue(privacyForm.usage_analytics, userSettings.usage_analytics);
    setCheckboxValue(privacyForm.crash_reports, userSettings.crash_reports);
  }

  // Display form
  if (displayForm) {
    setSelectValue(displayForm.theme, userSettings.theme);
    setSelectValue(displayForm.language, userSettings.language);
    setSelectValue(displayForm.timezone, userSettings.timezone);
  }

  // Apply theme
  applyTheme(userSettings.theme);
}

/**
 * Set checkbox value
 */
function setCheckboxValue(checkbox, value) {
  if (checkbox) {
    checkbox.checked = Boolean(value);
  }
}

/**
 * Set select value
 */
function setSelectValue(select, value) {
  if (select) {
    select.value = value || 'auto';
  }
}

/**
 * Handle notification settings
 */
async function handleNotificationSettings(event) {
  event.preventDefault();

  if (!supabase) return;

  const form = event.target;
  const formData = new FormData(form);

  const settings = {
    email_notifications: formData.get('email_notifications') === 'on',
    marketing_emails: formData.get('marketing_emails') === 'on',
    security_alerts: formData.get('security_alerts') === 'on'
  };

  setFormLoading(form, true);
  hideMessage();

  try {
    await saveSettings('notification', settings);
    showSuccess('Notification settings saved successfully!');

  } catch (error) {
    console.error('Failed to save notification settings:', error);
    showError('Failed to save notification settings. Please try again.');
  } finally {
    setFormLoading(form, false);
  }
}

/**
 * Handle privacy settings
 */
async function handlePrivacySettings(event) {
  event.preventDefault();

  if (!supabase) return;

  const form = event.target;
  const formData = new FormData(form);

  const settings = {
    profile_public: formData.get('profile_public') === 'on',
    usage_analytics: formData.get('usage_analytics') === 'on',
    crash_reports: formData.get('crash_reports') === 'on'
  };

  setFormLoading(form, true);
  hideMessage();

  try {
    await saveSettings('privacy', settings);
    showSuccess('Privacy settings saved successfully!');

  } catch (error) {
    console.error('Failed to save privacy settings:', error);
    showError('Failed to save privacy settings. Please try again.');
  } finally {
    setFormLoading(form, false);
  }
}

/**
 * Handle display settings
 */
async function handleDisplaySettings(event) {
  event.preventDefault();

  if (!supabase) return;

  const form = event.target;
  const formData = new FormData(form);

  const settings = {
    theme: formData.get('theme'),
    language: formData.get('language'),
    timezone: formData.get('timezone')
  };

  setFormLoading(form, true);
  hideMessage();

  try {
    await saveSettings('display', settings);

    // Apply theme immediately
    applyTheme(settings.theme);

    showSuccess('Display settings saved successfully!');

  } catch (error) {
    console.error('Failed to save display settings:', error);
    showError('Failed to save display settings. Please try again.');
  } finally {
    setFormLoading(form, false);
  }
}

/**
 * Save settings to database
 */
async function saveSettings(category, settings) {
  const updates = [];

  Object.entries(settings).forEach(([key, value]) => {
    updates.push({
      user_id: currentUser.id,
      k: `${category}.${key}`,
      v: value,
      updated_at: new Date().toISOString()
    });
  });

  // Use upsert to create or update settings
  const { error } = await supabase
    .from('settings')
    .upsert(updates, { onConflict: 'user_id,k' });

  if (error) {
    throw error;
  }

  // Update local state
  Object.assign(userSettings, settings);
}

/**
 * Apply theme
 */
function applyTheme(theme) {
  const root = document.documentElement;

  if (theme === 'dark') {
    root.style.setProperty('color-scheme', 'dark');
  } else if (theme === 'light') {
    root.style.setProperty('color-scheme', 'light');
  } else {
    // Auto - respect system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.style.setProperty('color-scheme', prefersDark ? 'dark' : 'light');
  }
}

/**
 * Handle theme change
 */
function handleThemeChange(event) {
  const theme = event.target.value;
  applyTheme(theme);
}

/**
 * Handle show API key
 */
function handleShowApiKey() {
  if (!showApiKeyBtn) return;

  if (apiKeyVisible) {
    // Hide API key
    showApiKeyBtn.textContent = 'Show';
    apiKeyVisible = false;
  } else {
    // Show API key (mock for now)
    showApiKeyBtn.textContent = 'Hide';
    apiKeyVisible = true;
    showInfo('API key visibility is a placeholder. Real API keys will be shown here.');
  }
}

/**
 * Handle regenerate API key
 */
async function handleRegenerateApiKey() {
  const confirmed = confirm('Are you sure you want to regenerate your API key? This will invalidate your current key.');

  if (!confirmed) return;

  if (!regenerateApiKeyBtn) return;

  const originalText = regenerateApiKeyBtn.textContent;
  regenerateApiKeyBtn.disabled = true;
  regenerateApiKeyBtn.textContent = 'Generating...';

  try {
    // Simulate API key generation
    await new Promise(resolve => setTimeout(resolve, 2000));

    showSuccess('API key regenerated successfully! Make sure to update your applications.');

  } catch (error) {
    console.error('Failed to regenerate API key:', error);
    showError('Failed to regenerate API key. Please try again.');
  } finally {
    regenerateApiKeyBtn.disabled = false;
    regenerateApiKeyBtn.textContent = originalText;
  }
}

/**
 * Handle configure webhooks
 */
function handleConfigureWebhooks() {
  showInfo('Webhook configuration will be available in a future update.');
}

/**
 * Handle export all data
 */
async function handleExportAllData() {
  if (!exportAllDataBtn) return;

  const originalText = exportAllDataBtn.textContent;
  exportAllDataBtn.disabled = true;
  exportAllDataBtn.textContent = 'Exporting...';

  try {
    // Gather all user data
    const exportData = {
      user: {
        id: currentUser.id,
        email: currentUser.email,
        created_at: currentUser.created_at
      },
      settings: userSettings,
      export_date: new Date().toISOString(),
      export_version: '1.0'
    };

    // Create download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orca-cloud-settings-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showSuccess('All settings exported successfully!');

  } catch (error) {
    console.error('Export error:', error);
    showError('Failed to export data. Please try again.');
  } finally {
    exportAllDataBtn.disabled = false;
    exportAllDataBtn.textContent = originalText;
  }
}

/**
 * Handle sign out
 */
async function handleSignOut() {
  try {
    if (!supabase) return;

    await supabase.auth.signOut();
    showSuccess('Signed out successfully. Redirecting...');

    // Redirect will be handled by auth state change listener

  } catch (error) {
    console.error('Sign out error:', error);
    showError('Failed to sign out. Please try again.');
  }
}

/**
 * Set form loading state
 */
function setFormLoading(form, loading) {
  const submitButton = form.querySelector('button[type="submit"]');
  const buttonText = submitButton.querySelector('.btn-text');
  const spinner = submitButton.querySelector('.btn-spinner');

  if (loading) {
    submitButton.disabled = true;
    submitButton.classList.add('loading');
    if (buttonText) buttonText.textContent = 'Saving...';
  } else {
    submitButton.disabled = false;
    submitButton.classList.remove('loading');

    // Restore original text based on form
    if (form.id === 'notification-form') {
      buttonText.textContent = 'Save Notification Settings';
    } else if (form.id === 'privacy-form') {
      buttonText.textContent = 'Save Privacy Settings';
    } else if (form.id === 'display-form') {
      buttonText.textContent = 'Save Display Settings';
    }
  }
}

/**
 * Show success message
 */
function showSuccess(text) {
  showMessage(text, 'success');
}

/**
 * Show error message
 */
function showError(text) {
  showMessage(text, 'error');
}

/**
 * Show info message
 */
function showInfo(text) {
  showMessage(text, 'info');
}

/**
 * Show message
 */
function showMessage(text, variant = 'info') {
  if (!messageBox) return;

  messageBox.textContent = text;
  messageBox.dataset.variant = variant;
  messageBox.classList.add('is-visible');

  // Auto-hide success and info messages after 5 seconds
  if (variant === 'success' || variant === 'info') {
    setTimeout(() => {
      hideMessage();
    }, 5000);
  }
}

/**
 * Hide message
 */
function hideMessage() {
  if (!messageBox) return;

  messageBox.classList.remove('is-visible');
  messageBox.textContent = '';
  messageBox.removeAttribute('data-variant');
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initSettings);